import * as pdfjsLib from 'pdfjs-dist'
export type PdfLink = {
  page: number
  url: string
  label: string
  rect: { left: number; right: number; top: number; bottom: number }
}

interface TextItem {
  str: string
  width: number
  transform: number[]
}

interface AnnotationData {
  subtype: string
  url?: string
  rect?: [number, number, number, number]
  quadPoints?: number[]
}

export async function extractPdfLinksFromBlob(
  blob: Blob,
  textMatcher: string | RegExp,
): Promise<PdfLink[]> {
  const data = await blob.arrayBuffer()
  const loadingTask = pdfjsLib.getDocument({ data })
  const pdf = await loadingTask.promise

  const matcher =
    textMatcher instanceof RegExp
      ? textMatcher
      : new RegExp(textMatcher.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')

  const results: PdfLink[] = []

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum)
    const viewport = page.getViewport({ scale: 1 })

    const annots = await page.getAnnotations({ intent: 'display' }) // includes link rects/urls
    const textContent = await page.getTextContent() // words + transforms

    // Precompute text bboxes (viewport coords) for quick overlap tests
    const textItems = textContent.items.map((it) => {
      const { transform, width, str } = it as TextItem
      const [_a, b, _c, d, e, f] = transform
      const height = Math.hypot(b, d) || 0
      const rect = [e, f, e + width, f + height] as [
        number,
        number,
        number,
        number,
      ]
      const [vx1, vy1, vx2, vy2] = viewport.convertToViewportRectangle(rect)
      return {
        str: str || '',
        bbox: {
          left: Math.min(vx1, vx2),
          right: Math.max(vx1, vx2),
          top: Math.max(vy1, vy2),
          bottom: Math.min(vy1, vy2),
        },
      }
    })

    const overlaps = (
      r1: { left: number; right: number; top: number; bottom: number },
      r2: { left: number; right: number; top: number; bottom: number },
    ) =>
      !(
        r2.left > r1.right ||
        r2.right < r1.left ||
        r2.bottom > r1.top ||
        r2.top < r1.bottom
      )

    for (const a of annots) {
      const annot = a as AnnotationData
      if (annot.subtype !== 'Link') continue
      const href = annot.url || null // skip internal 'dest' if you only want external
      if (!href) continue

      // Use quadPoints if present; otherwise rect
      let rect = annot.rect as [number, number, number, number]
      const qp = annot.quadPoints
      if (Array.isArray(qp) && qp.length >= 8) {
        const xs = [qp[0], qp[2], qp[4], qp[6]]
        const ys = [qp[1], qp[3], qp[5], qp[7]]
        rect = [
          Math.min(...xs),
          Math.min(...ys),
          Math.max(...xs),
          Math.max(...ys),
        ]
      }
      const [vx1, vy1, vx2, vy2] = viewport.convertToViewportRectangle(rect)
      const linkBox = {
        left: Math.min(vx1, vx2),
        right: Math.max(vx1, vx2),
        top: Math.max(vy1, vy2),
        bottom: Math.min(vy1, vy2),
      }

      // Collect overlapping text and build a label
      const pieces: string[] = []
      for (const ti of textItems)
        if (ti.str && overlaps(linkBox, ti.bbox)) pieces.push(ti.str)
      const label = pieces.join(' ').replace(/\s+/g, ' ').trim()

      if (label && matcher.test(label)) {
        results.push({ page: pageNum, url: href, label, rect: linkBox })
      }
    }
  }

  return results
}
